import { defaultError } from '@qpr/client-library';
import toposort from 'toposort';
import api, { ApiRequest, ApiResult } from '../../../api/api-caller';
import { ApiEndpoint } from '../../../api/api-consts';
import { fetchRangeColors, getRangeColors, RangeColor } from '../../../utils/rangecolor-utils';
import {
  CanvasCellState as CanvasCell,
  CellRowState as CellRow,
  initialLowerState,
  initialUpperState
} from '../sipoc-state';

/** View Model for SIPOC Page */

interface BusinessProcess {
  id: string;
  name: string;
}

interface ChildObject {
  name: string;
  typename: string;
  from: string;
  to: string;
  sipocViewId: string;
  id: string;
  fullid: string;
  measureId: string;
  rangecolor?: string;
}

const errorHandler = (state: any) => (apiResults: ApiResult[]) => {
  const apiErrors = apiResults.filter(apiResult => apiResult.error);
  return { ...state, inited: true, error: apiErrors.pop()?.error || defaultError };
};

const lowerErrorHandler = errorHandler(initialLowerState);
const upperErrorHandler = errorHandler(initialUpperState);

const fetchSubProcesses = async (sipocID: string): Promise<ApiResult> => {
  const childObjectsQuery: ApiRequest = {
    body: {
      query: `[${sipocID}].childobjects(criteria="Typename=\\"Business Process\\"").related(keeporiginals=1)`,
      criteria: 'typename="Business Process" OR typename = "Triggering"',
      attributes:
        'name, typename, functionasattribute(functionasattribute="from", as="from"), functionasattribute(functionasattribute="to", as="to"), RelationEnds.AssociationFrom(as=sipocViewId), id, fullid, functionasattribute(functionasattribute="scorecardlinks", as="measureId")'
    },
    method: 'POST'
  };

  const childObjectsResult: ApiResult = await api.asyncCallApi(
    ApiEndpoint.meaquery,
    childObjectsQuery
  );

  return childObjectsResult;
};

const getSubProcesses = (childObjectsResult: ApiResult): ChildObject[] => {
  const childObjects = childObjectsResult.data;

  const subProcesses = childObjects.reduce(
    (a: ChildObject[], o: ChildObject) => (o.typename === 'Business Process' ? a.concat(o) : a),
    []
  );

  // take trueSubProcesses and loop through
  // to find the items in full list to find what items have the current item's id in attribute "from"
  // create an array where items are [group's id, ]

  const processGraph: [string, string | undefined][] = [];

  for (let i = 0; i < subProcesses.length; i += 1) {
    const toId = childObjects.find((item: ChildObject) => item.from === subProcesses[i].fullid).to;
    const orderNode: [string, string | undefined] = [subProcesses[i].fullid, toId];
    processGraph.push(orderNode);
  }

  const sortOrder = toposort(processGraph);

  subProcesses.sort((a: ChildObject, b: ChildObject) => {
    return sortOrder.indexOf(a.fullid) - sortOrder.indexOf(b.fullid);
  });

  return subProcesses;
};

const fetchHeader = async (sipocID: string): Promise<ApiResult> => {
  const sipocDiagramQuery: ApiRequest = {
    body: {
      query: `[${sipocID}]`,
      attributes: 'RelationEnds.AssociationTo.value(as=name), RelationEnds.AssociationTo(as=id)'
    },
    method: 'POST'
  };
  const sipocDiagramResult: ApiResult = await api.asyncCallApi(
    ApiEndpoint.meaquery,
    sipocDiagramQuery
  );

  return sipocDiagramResult;
};

const getHeader = (headerResult: ApiResult): BusinessProcess => {
  return headerResult.data.pop();
};

const fetchSipocGroups = async (sipocID: string): Promise<ApiResult> => {
  const sipocGroupsQuery: ApiRequest = {
    body: {
      parentId: sipocID,
      objecttype: 'Group',
      attributes: 'name, RelationEnds.AssociationFrom(as=sipoc_view_id)'
    },
    method: 'POST'
  };
  const sipocGroupsResult: ApiResult = await api.asyncCallApi(
    ApiEndpoint.childelements,
    sipocGroupsQuery
  );

  return sipocGroupsResult;
};

const getSipocGroups = (sipocGroupsResult: ApiResult): CanvasCell[] => {
  return sipocGroupsResult.data.map((group: CanvasCell) => {
    return { ...group, rows: [] };
  });
};

const getSipocGroupItems = (sipocItemResult: ApiResult): CellRow[] => {
  return sipocItemResult.data;
};

const fetchSipocGroupItems = async (sipocGroupIds: String[]): Promise<ApiResult> => {
  const groupItemQuery: ApiRequest = {
    body: {
      parentId: sipocGroupIds.join(),
      groupelements: true,
      attributes:
        'name,relationends.belongstogroup(as=groupId), functionasattribute(functionasattribute="scorecardlinks", as="measureId")',
      sortby: 'name'
    },
    method: 'POST'
  };

  const groupItemsResult: ApiResult = await api.asyncCallApi(
    ApiEndpoint.childelements,
    groupItemQuery
  );

  return groupItemsResult;
};

type WithRangeColor = {
  measureId?: string;
  rangecolor?: string;
};

const addRangeColorsToItems = <T extends WithRangeColor>(
  rangeColors: RangeColor[],
  items: T[]
): void => {
  rangeColors.forEach(rangeColor => {
    const foundList = items.filter(item => item.measureId === rangeColor.id);

    if (foundList.length > 0) {
      for (let i = 0; i < foundList.length; i += 1) {
        foundList[i].rangecolor =
          rangeColor.rangecolor === '000000' ? undefined : `#${rangeColor.rangecolor}`;
      }
    }
  });
};

const addGroupItemsToGroups = (groupItems: any, groups: any): void => {
  groups.forEach((group: any) => {
    groupItems.forEach((groupItem: any) => {
      if (groupItem.groupId && groupItem.groupId.includes(group.id as string)) {
        group.rows.push(groupItem);
      }
    });
  });
};

const fetchSipocId = async (businessProcessId: string): Promise<ApiResult> => {
  const sipocDiagramQuery: ApiRequest = {
    body: {
      query: `[${businessProcessId}]`,
      attributes: 'RelationEnds.AssociationFrom(as=sipoc_view_id)'
    },
    method: 'POST'
  };
  const sipocDiagramResult: ApiResult = await api.asyncCallApi(
    ApiEndpoint.meaquery,
    sipocDiagramQuery
  );

  return sipocDiagramResult;
};

const getSipocId = (sipocIdResult: ApiResult): string => {
  return sipocIdResult.data.pop().sipoc_view_id;
};

const getUpperPart = async (businessProcessID: string): Promise<typeof initialUpperState> => {
  try {
    const subProcessesResult = await fetchSubProcesses(businessProcessID);

    if (!subProcessesResult.ok) {
      return upperErrorHandler([subProcessesResult]);
    }

    const subProcesses = getSubProcesses(subProcessesResult);

    if (subProcesses.length === 0)
      return {
        ...initialUpperState,
        data: [],
        inited: true,
        error: {
          message: 'No end-to-end process definition found for the SIPOC view.',
          details: `No end-to-end process definition found for the SIPOC view for Business process id ${businessProcessID}`
        }
      };

    const measureIds = subProcesses
      .map((subProcess: any) => subProcess.measureId)
      .filter((measureId: string) => measureId !== undefined && measureId !== '');

    if (measureIds.length > 0) {
      const rangeColorsResult = await fetchRangeColors(measureIds);

      if (!rangeColorsResult.ok) {
        return upperErrorHandler([rangeColorsResult]);
      }

      const rangeColors = getRangeColors(rangeColorsResult);
      addRangeColorsToItems(rangeColors, subProcesses);
    }
    const data = subProcesses.map((e: ChildObject) => {
      return {
        title: e.name,
        url: e.sipocViewId ? `../${e.sipocViewId}/` : '',
        id: e.id,
        sipocid: e.sipocViewId,
        rangecolor: e.rangecolor
      };
    });

    return { ...initialUpperState, data, inited: true };
  } catch (error) {
    return {
      ...initialUpperState,
      inited: true,
      error: { message: 'Server unreachable', details: JSON.stringify(error) }
    };
  }
};

const getLowerPart = async (
  businessProcessID: string,
  parameterSipocID: string
): Promise<typeof initialLowerState> => {
  try {
    let sipocId = parameterSipocID;

    if (!parameterSipocID || parameterSipocID === '-') {
      const sipocIdResult = await fetchSipocId(businessProcessID);
      if (!sipocIdResult.ok) {
        return lowerErrorHandler([sipocIdResult]);
      }

      sipocId = getSipocId(sipocIdResult);
      console.log('no sipocId given, fetched main process SipocId', sipocId);
    }
    if (!sipocId)
      return {
        ...initialLowerState,
        inited: true,
        error: {
          message: 'No Sipoc data found.',
          details: `No related Sipoc data found for Business process id ${businessProcessID}`
        }
      };

    const [headerResult, sipocGroupsResult] = await Promise.all([
      fetchHeader(sipocId),
      fetchSipocGroups(sipocId)
    ]);

    if (!headerResult.ok || !sipocGroupsResult.ok) {
      return lowerErrorHandler([headerResult, sipocGroupsResult]);
    }

    const sipocGroups = getSipocGroups(sipocGroupsResult);

    if (sipocGroups.length === 0)
      return {
        ...initialLowerState,
        inited: true,
        error: {
          message: 'No Sipoc data found.',
          details: `No related Sipoc data found for Sipoc id ${sipocId}`
        }
      };

    const sipocGroupIds = sipocGroups.map(sipocGroup => sipocGroup.id);

    const sipocGroupItemsResult = await fetchSipocGroupItems(sipocGroupIds);
    if (!sipocGroupItemsResult.ok) {
      return lowerErrorHandler([sipocGroupItemsResult]);
    }

    const sipocGroupItems = getSipocGroupItems(sipocGroupItemsResult);

    const sipocGroupItemMeasureIds = sipocGroupItems
      .map((sipocGroupItem: any) => sipocGroupItem.measureId)
      .filter(
        (sipocGroupItemMeasureId: string) =>
          sipocGroupItemMeasureId !== undefined && sipocGroupItemMeasureId !== ''
      );

    if (sipocGroupItemMeasureIds.length > 0) {
      const rangeColorResult = await fetchRangeColors(sipocGroupItemMeasureIds);

      if (!rangeColorResult.ok) {
        return lowerErrorHandler([rangeColorResult]);
      }

      const rangeColors = getRangeColors(rangeColorResult);

      addRangeColorsToItems(rangeColors, sipocGroupItems);
    }
    addGroupItemsToGroups(sipocGroupItems, sipocGroups);

    const header = getHeader(headerResult);

    return {
      ...initialLowerState,
      data: sipocGroups,
      header: header.name,
      inited: true
    };
  } catch (error) {
    return {
      ...initialLowerState,
      error: { message: 'Server unreachable', details: JSON.stringify(error) },
      inited: true
    };
  }
};

// allow use string to access element
interface IData {
  [key: string]: any;
}
const viewModelCache: IData = {};

export interface SIPOCViewModelType {
  fetchUpperData(): Promise<typeof initialUpperState>;
  getUpperData(): Promise<typeof initialUpperState>;
  fetchLowerData(): Promise<typeof initialLowerState>;
  getLowerData(): Promise<typeof initialLowerState>;
  getId(): {};
  businessProcessId: string;
  sipocId: string;
}

const createSIPOCCanvasViewModel = (
  businessProcessId: string,
  sipocId: string
): SIPOCViewModelType => {
  let upperData: typeof initialUpperState;
  let lowerData: typeof initialLowerState;
  const getId = (): {} => {
    return {
      businessProcessId,
      sipocId
    };
  };

  const fetchUpperData = async (): Promise<typeof initialUpperState> => {
    const cacheKey = `U:${businessProcessId}`;
    if (viewModelCache[cacheKey]) {
      console.log('From cache', viewModelCache[cacheKey]);
      upperData = viewModelCache[cacheKey];
    } else {
      upperData = await getUpperPart(businessProcessId);
      viewModelCache[cacheKey] = upperData;
    }
    return upperData;
  };

  const getUpperData = async (): Promise<typeof initialUpperState> => {
    if (upperData) {
      console.log('getData already populated');
      return upperData;
    }
    await fetchUpperData();
    return upperData;
  };

  const fetchLowerData = async (): Promise<typeof initialLowerState> => {
    const cacheKey = `L:${businessProcessId}-${sipocId}`;
    if (viewModelCache[cacheKey]) {
      console.log('From cache', viewModelCache[cacheKey]);
      lowerData = viewModelCache[cacheKey];
    } else {
      lowerData = await getLowerPart(businessProcessId, sipocId);
      viewModelCache[cacheKey] = lowerData;
    }
    return lowerData;
  };

  const getLowerData = async (): Promise<typeof initialLowerState> => {
    if (lowerData) {
      console.log('getData already populated');
      return lowerData;
    }
    await fetchLowerData();
    return lowerData;
  };

  return {
    getId,
    fetchUpperData,
    getUpperData,
    fetchLowerData,
    getLowerData,
    sipocId,
    businessProcessId
  };
};

export default createSIPOCCanvasViewModel;
